import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { QuizAssessmentLandingPageDetails } from '@edtech-ui/core/models/quiz-landing-page/get-all-quiz-link';
import { FormsModule } from '@angular/forms';
import { DeepLinkUtil } from '@edtech-ui/core/utils/deep-link.util';

@Component({
	selector: 'app-public-mock-test-list',
	standalone: true,
	imports: [CommonModule, RouterModule, FormsModule],
	templateUrl: './public-mock-test-list.component.html',
	styleUrl: './public-mock-test-list.component.scss'
})
export class PublicMockTestListComponent implements OnInit, OnDestroy {

	mockTest: QuizAssessmentLandingPageDetails[] = [];
	isLoading = false;
	isLoadingMore = false;
	currentPage = 1;
	hasMore = true;
	totalTests = 0;
	searchQuery = '';
	selectedCategory = 'All';
	selectedRole = '';
	roles: string[] = [];
	categories: string[] = [];


	private searchSubject = new Subject<string>();
	private subscription = new Subscription();
	isBrowser: boolean;
	isDashboard = false;

	constructor(
		private quizService: QuizManagementService,
		@Inject(PLATFORM_ID) platformId: Object,
		private router: Router
	) {
		this.isBrowser = isPlatformBrowser(platformId);
	}


	ngOnInit() {
		const token = localStorage.getItem('token');
		if (token && !this.router.url.includes('student-dashboard')) {
			this.router.navigate(['/student-dashboard/mock-test']);
			return;
		}
		this.isDashboard = !!token;

		this.subscription.add(
			this.searchSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe(query => {
				this.searchQuery = query;
				this.fetchMockTests(true);
			}));
		this.fetchMockTests(true);
	}


	onSearch(event: any) {
		this.searchSubject.next(event.target.value);
	}


	onCategoryChange(event: any) {
		this.selectedCategory = event.target.value;
		this.fetchMockTests(true);
	}


	onRoleChange(event: any) {
		this.selectedRole = event.target.value;
		this.fetchMockTests(true);
	}


	fetchMockTests(isInitial = false) {
		if (isInitial) {
			this.isLoading = true;
			this.currentPage = 1;
			this.mockTest = [];
		} else {
			this.isLoadingMore = true;
		}

		const categoryParam = this.selectedCategory === 'ALL' || !this.selectedCategory ? undefined : this.selectedCategory;
		const roleParam = !this.selectedRole ? undefined : this.selectedRole;
		const searchParam = !this.searchQuery ? undefined : this.searchQuery;

		this.subscription.add(
			this.quizService.getQuizAssessmentLandingPage(
				this.currentPage,
				9,
				true,
				undefined,
				undefined,
				searchParam,
				categoryParam,
				roleParam
			).subscribe({
				next: (response: any) => {
					if (response.Success && response.Data) {
						this.roles = response.Data.filters.roles;
						this.categories = response.Data.filters.categories;
						const newQuizzes = response.Data.data || [];
						this.mockTest = [...this.mockTest, ...newQuizzes];

						if (response.Data.pagination) {
							this.totalTests = response.Data.pagination.totalLinks || 0;
							this.hasMore = response.Data.pagination.hasNextPage;
						} else {
							this.hasMore = newQuizzes.length === 9;
						}
					}
					this.isLoading = false;
					this.isLoadingMore = false;
				},
				error: (err) => {
					this.isLoading = false;
					this.isLoadingMore = false;
				}
			}));
	}


	loadMore() {
		if (!this.hasMore || this.isLoadingMore) return;
		this.currentPage++;
		this.fetchMockTests();
	}


	trackByTestId(index: number, test: any): string {
		return test?.link_id || test?._id || index.toString();
	}


	getTestDuration(test: QuizAssessmentLandingPageDetails): string {
		// time_limit is stored in seconds.
		const totalSeconds = test?.time_limit;

		if (!totalSeconds || totalSeconds <= 0) {
			return '';
		}

		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;

		const parts: string[] = [];
		if (hours > 0) parts.push(`${hours} hr`);
		if (minutes > 0) parts.push(`${minutes} min`);
		if (seconds > 0) parts.push(`${seconds} sec`);

		return parts.join(' ');
	}


	startMockTest(test: QuizAssessmentLandingPageDetails) {
		// Deep Linking: Try to launch the Android app into the placement prep dashboard
		// instead of navigating on the web for mobile users.
		if (DeepLinkUtil.isAndroid()) {
			window.location.href = DeepLinkUtil.getAndroidIntentUrl('student-dashboard/placement-prep');
			return;
		}
		const token = localStorage.getItem('token');
		if (!token) {
			this.router.navigate(['/login'], { queryParams: { redirectUrl: '/student-dashboard/placement-prep' } });
			return;
		}
		this.router.navigate(['/mock-test', test.link_id || test._id]);
	}


	ngOnDestroy() {
		this.subscription.unsubscribe();
		this.searchSubject.complete();
	}

}
